import { useEffect, useMemo, useState } from "react";
import { CircularProgress, Paper, Stack, Typography } from "@mui/material";
import AccountBalanceWalletOutlined from "@mui/icons-material/AccountBalanceWalletOutlined";
import AttachMoneyRounded from "@mui/icons-material/AttachMoneyRounded";
import BarChartRounded from "@mui/icons-material/BarChartRounded";
import BusinessRounded from "@mui/icons-material/BusinessRounded";
import Inventory2Outlined from "@mui/icons-material/Inventory2Outlined";
import PercentRounded from "@mui/icons-material/PercentRounded";
import TrendingDownRounded from "@mui/icons-material/TrendingDownRounded";
import AgenteContabilidadeShell from "../components/AgenteContabilidadeShell";
import ChartCard from "../components/ChartCard";
import EmpreendimentosIntersecaoNotice from "../components/EmpreendimentosIntersecaoNotice";
import FilterSelect from "../components/FilterSelect";
import KpiGrid from "../components/KpiGrid";
import MonthFallbackNotice from "../components/MonthFallbackNotice";
import PeriodSelector from "../components/PeriodSelector";
import { useAgenteContabilidadeStore } from "../hooks/useAgenteContabilidadeStore";
import { usePageEmpresaSelection } from "../hooks/usePageEmpresaSelection";
import { usePageMonthSelection } from "../hooks/usePageMonthSelection";
import { logContabilidadeApiError, obterCustos } from "../services/agenteContabilidadeApiService";
import {
  buildEmpresaFilterParams,
  buildEmpreendimentoOptions,
  findEmpreendimentoByFilterValue,
  isTodosEmpreendimentos,
  normalizeEmpreendimentoContext,
  TODOS_EMPREENDIMENTOS_VALUE,
} from "../utils/empreendimentoSelector";
import {
  consolidateTimelineByMonth,
  getIntersecaoForMonth,
  normalizeIntersecaoList,
  normalizeIntersecaoPorMes,
  resolveActiveMonth,
} from "../utils/intersecaoEmpreendimentos";
import { colors } from "../constants/designTokens";
import { formatCurrency, formatPercent } from "../utils/formatters";

const getFirstValue = (item, keys, fallback = 0) => {
  for (const key of keys) {
    if (item?.[key] !== undefined && item?.[key] !== null) {
      return item[key];
    }
  }

  return fallback;
};

const toNumber = (value) => {
  const numberValue = Number(value || 0);
  return Number.isFinite(numberValue) ? numberValue : 0;
};

function normalizeCustosItem(item) {
  return {
    mes_ano: getFirstValue(item, ["mes_ano", "mesAno", "competencia", "mes"], ""),
    idClienteEmpresa: getFirstValue(item, ["idClienteEmpresa", "id_cliente_empresa"], ""),
    nomeEmpresa: getFirstValue(item, ["nomeEmpresa", "nome_empresa"], ""),
    pctVendido: toNumber(getFirstValue(item, ["pctVendido", "percentualVendido", "percentual_vendido"])),
    poc: toNumber(getFirstValue(item, ["poc", "pocExecucao", "poc_execucao"])),
    custoOrcado: toNumber(getFirstValue(item, ["custoOrcado", "custo_orcado"])),
    custoAIncorrer: toNumber(getFirstValue(item, ["custoAIncorrer", "custo_a_incorrer"])),
    incorridoAcumulado: toNumber(getFirstValue(item, ["incorridoAcumulado", "incorrido_acumulado"])),
    custoApropriado: toNumber(getFirstValue(item, ["custoApropriado", "custo_apropriado"])),
    estoque: toNumber(getFirstValue(item, ["estoque"])),
    custoARealizar: toNumber(getFirstValue(item, ["custoARealizar", "custo_a_realizar"])),
  };
}

function normalizeCustosResponse(payload) {
  if (!payload) {
    return null;
  }

  const sourceTimeline =
    (Array.isArray(payload) && payload) ||
    payload.timeline ||
    payload.grafico ||
    payload.registros ||
    payload.items ||
    payload.data ||
    [];

  if (!Array.isArray(sourceTimeline)) {
    return null;
  }

  const timeline = sourceTimeline.map(normalizeCustosItem).filter((item) => item.mes_ano);
  const months = Array.isArray(payload.meses)
    ? payload.meses
    : Array.isArray(payload.months)
    ? payload.months
    : timeline.map((item) => item.mes_ano);

  return {
    months,
    timeline,
    empreendimentosIntersecao: normalizeIntersecaoList(
      payload.empreendimentosIntersecao ||
        payload.empreendimentos_intersecao ||
        payload.intersecaoEmpreendimentos ||
        payload.intersecao_empreendimentos
    ),
    empreendimentosIntersecaoPorMes: normalizeIntersecaoPorMes(payload),
  };
}

export default function CustosPage() {
  const { state, actions } = useAgenteContabilidadeStore();
  const [selectedMonth, setSelectedMonth] = usePageMonthSelection("custos");
  const [apiAvailableMonths, setApiAvailableMonths] = useState([]);
  const [apiCustosData, setApiCustosData] = useState(null);
  const [apiStatus, setApiStatus] = useState("idle");

  const backendEmpreendimentos = useMemo(
    () => state.backendCatalog?.empreendimentos || [],
    [state.backendCatalog?.empreendimentos]
  );
  const backendCatalogSyncKey = state.backendCatalog?.lastSyncAt || "";
  const hasBackendEmpreendimentos = backendEmpreendimentos.length > 0;
  const backendEmpresaOptions = useMemo(
    () => buildEmpreendimentoOptions(backendEmpreendimentos),
    [backendEmpreendimentos]
  );
  const [selectedBackendEmpresa, setSelectedBackendEmpresa] = usePageEmpresaSelection("custos", backendEmpresaOptions);

  useEffect(() => {
    actions.refreshBackendCatalog().catch(() => {});
  }, [actions]);

  useEffect(() => {
    let active = true;

    async function fetchCustos() {
      if (!backendCatalogSyncKey && !selectedBackendEmpresa) {
        setApiStatus("loading");
        return;
      }

      if (hasBackendEmpreendimentos && !selectedBackendEmpresa) {
        setApiStatus("loading");
        return;
      }

      setApiStatus("loading");

      try {
        const params = buildEmpresaFilterParams(selectedBackendEmpresa);
        const payload = await obterCustos(params);
        const normalizedData = normalizeCustosResponse(payload);

        if (!active) return;

        if (normalizedData?.timeline?.length) {
          setApiAvailableMonths(normalizedData.months);
          setApiCustosData(normalizedData);
          setApiStatus("success");
        } else {
          setApiCustosData(null);
          setApiStatus("empty");
        }
      } catch (error) {
        if (!active) return;
        logContabilidadeApiError("custos", error);
        setApiStatus("error");
      }
    }

    fetchCustos();

    return () => {
      active = false;
    };
  }, [backendCatalogSyncKey, hasBackendEmpreendimentos, selectedBackendEmpresa]);

  const usingApiData = Boolean(apiCustosData?.timeline?.length);
  const custosData = usingApiData ? apiCustosData : { months: [], timeline: [] };
  const { timeline } = custosData;
  const isTodos = isTodosEmpreendimentos(selectedBackendEmpresa);
  const visibleTimeline = useMemo(
    () => (isTodos ? consolidateTimelineByMonth(timeline, ["pctVendido", "poc"]) : timeline),
    [isTodos, timeline]
  );
  const months = usingApiData && apiAvailableMonths.length
    ? apiAvailableMonths
    : custosData.months;

  const activeMonth = resolveActiveMonth(months, selectedMonth);

  const currentSnapshot = visibleTimeline.find((item) => item.mes_ano === activeMonth) || visibleTimeline[visibleTimeline.length - 1];
  const intersecaoEmpreendimentos = getIntersecaoForMonth(custosData, activeMonth);
  const selectedEmpreendimentoContext = isTodos
    ? { filtro: TODOS_EMPREENDIMENTOS_VALUE, nomeEmpresa: "Todos Empreendimentos" }
    : normalizeEmpreendimentoContext(findEmpreendimentoByFilterValue(backendEmpreendimentos, selectedBackendEmpresa));

  const empreendimentoOptions = backendEmpresaOptions;
  const selectedEmpreendimentoValue = selectedBackendEmpresa;

  function handleEmpreendimentoChange(value) {
    setSelectedMonth((current) => current || activeMonth);
    setSelectedBackendEmpresa(value || "");
    setApiAvailableMonths([]);
  }

  const metrics = currentSnapshot
    ? [
        { label: "% Vendido", value: formatPercent(currentSnapshot.pctVendido, { decimals: 2 }), variant: "purple", icon: PercentRounded },
        { label: "% POC - Percentage of Completion", value: formatPercent(currentSnapshot.poc, { decimals: 2 }), variant: "blue", icon: TrendingDownRounded },
        { label: "Custo Orcado", value: formatCurrency(currentSnapshot.custoOrcado), variant: "orange", icon: AttachMoneyRounded },
        { label: "Custo Orcado a Incorrer", value: formatCurrency(currentSnapshot.custoAIncorrer), variant: "red", icon: Inventory2Outlined },
        { label: "Incorrido Acumulado", value: formatCurrency(currentSnapshot.incorridoAcumulado), variant: "green", icon: AccountBalanceWalletOutlined },
        { label: "Custo das Vendas Apropriado", value: formatCurrency(currentSnapshot.custoApropriado), variant: "teal", icon: BarChartRounded },
        { label: "Estoque", value: formatCurrency(currentSnapshot.estoque), variant: "navy", icon: Inventory2Outlined },
        { label: "Custo a Realizar", value: formatCurrency(currentSnapshot.custoARealizar), variant: "magenta", icon: AttachMoneyRounded },
      ]
    : [];

  const chartData = visibleTimeline.map((item) => ({
    mes_ano: item.mes_ano,
    custoOrcado: item.custoOrcado / 1000000,
    incorridoAcumulado: item.incorridoAcumulado / 1000000,
    custoApropriado: item.custoApropriado / 1000000,
  }));
  const isLoadingCustos = (apiStatus === "idle" || apiStatus === "loading") && !usingApiData;

  return (
    <AgenteContabilidadeShell
      title="Custo do Empreendimento"
      subtitle="Análise mensal do custo do empreendimento"
      updating={apiStatus === "loading" && usingApiData}
      agenteContext={{
        paginaAtual: "custos",
        empresa: selectedBackendEmpresa,
        competencia: activeMonth,
        dadosVisiveis: currentSnapshot
          ? {
              percentualVendido: currentSnapshot.pctVendido,
              poc: currentSnapshot.poc,
              custoOrcado: currentSnapshot.custoOrcado,
              custoAIncorrer: currentSnapshot.custoAIncorrer,
              incorridoAcumulado: currentSnapshot.incorridoAcumulado,
              custoApropriado: currentSnapshot.custoApropriado,
              estoque: currentSnapshot.estoque,
              custoARealizar: currentSnapshot.custoARealizar,
              empresaSelecionada: selectedEmpreendimentoContext,
            }
          : {},
      }}
      actions={[
        <FilterSelect
          key="emp"
          value={selectedEmpreendimentoValue}
          onChange={handleEmpreendimentoChange}
          options={empreendimentoOptions}
          icon={BusinessRounded}
          minWidth={286}
        />,
      ]}
    >
      {isLoadingCustos ? (
        <Paper elevation={0} sx={{ borderRadius: 3, bgcolor: colors.surface, p: 4, border: `1px solid ${colors.border}` }}>
          <Stack alignItems="center" spacing={1.5} sx={{ textAlign: "center" }}>
            <CircularProgress size={34} />
            <Typography sx={{ fontWeight: 900, color: colors.text }}>
              Carregando dados de custos
            </Typography>
            <Typography sx={{ color: colors.muted, fontSize: 13 }}>
              Aguarde enquanto consultamos os indicadores calculados pela API.
            </Typography>
          </Stack>
        </Paper>
      ) : !visibleTimeline.length ? (
        <Paper elevation={0} sx={{ borderRadius: 3, bgcolor: colors.surface, p: 4, border: `1px solid ${colors.border}` }}>
          <Stack alignItems="center" spacing={1.2} sx={{ textAlign: "center" }}>
            <Typography sx={{ fontWeight: 900, color: colors.text, fontSize: 20 }}>
              Nenhum dado disponível
            </Typography>
            <Typography sx={{ color: colors.muted, fontSize: 14, maxWidth: 520 }}>
              Não encontramos dados disponíveis para exibição no momento.
            </Typography>
          </Stack>
        </Paper>
      ) : (
        <>
          <PeriodSelector months={months} value={activeMonth} onChange={setSelectedMonth} />
          <MonthFallbackNotice requestedMonth={selectedMonth} activeMonth={activeMonth} />
          <EmpreendimentosIntersecaoNotice
            isTodosEmpreendimentos={isTodos}
            activeMonth={activeMonth}
            empreendimentos={intersecaoEmpreendimentos}
          />
          <KpiGrid metrics={metrics} />
          <ChartCard
            title="Evolução dos Custos"
            data={chartData}
            series={[
              { key: "custoOrcado", name: "Custo Orcado", color: "#f59e0b" },
              { key: "incorridoAcumulado", name: "Incorrido Acumulado", color: "#14b8a6" },
              { key: "custoApropriado", name: "Custo Apropriado", color: "#6366f1" },
            ]}
            headerGradient="green"
          />
        </>
      )}
    </AgenteContabilidadeShell>
  );
}
